"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformOperation = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const consolidateErrors_1 = require("./consolidateErrors");
const errorUtils_1 = require("./errorUtils");
const utils_1 = require("./utils");
const consolidateFormulaErrors = (formulaErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(formulaErrors, (error) => error.formula, // Group by formula
    (error) => ({
        message: error.message,
        row: String(error.row),
    }), // Map to row data
    (formula) => `Formula [${formula}] has errors`);
};
const consolidateFilterValidationErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.message, // Group by message
    (error) => ({
        row: String(error.row),
    }), // Map to row data
    (message) => `${message}`);
};
const consolidateToStructuredErrors = (errors) => {
    const unknownErrors = errors.filter((error) => error.type === 'filterValidation');
    const formulaErrors = errors.filter((error) => error.type === 'filterFormulaEvaluationError');
    // we want to consolidate the errors if there are too many of those.
    return [
        ...consolidateFormulaErrors(formulaErrors),
        ...consolidateFilterValidationErrors(unknownErrors),
    ];
};
const checkPreErrors = (transformation) => {
    // At the moment, just checking against formulas and available formulas
    // for FormulaEngine usage when assessing output dataTape
    const { filterFormula, srcDataTape } = transformation;
    const errors = [];
    const availableVariables = new Set();
    for (const { field } of srcDataTape.baseFields) {
        availableVariables.add(field);
    }
    const { errorMessages } = (0, formula_engine_1.getFormulaErrors)(filterFormula, availableVariables);
    for (const message of errorMessages) {
        errors.push({ type: 'metadataValidation', message });
    }
    return errors;
};
const validateTransform = (transformation) => {
    const preErrors = checkPreErrors(transformation);
    if (preErrors.length) {
        return {
            isValid: false,
            errors: preErrors,
        };
    }
    // Since only just filtering the rows down and not impacting
    // any actual fields or values, can just return provided srcDataTape
    return {
        isValid: true,
        data: transformation.srcDataTape,
    };
};
const processFilter = async (params) => {
    const { formulaEngine, transformation, inputCsvArray } = params;
    const outputData = [];
    const outputErrors = [];
    inputCsvArray.forEach((data, idx) => {
        try {
            formulaEngine.setVariables(data);
            const isDataFilterCompliant = formulaEngine.evaluateBoolean(transformation.filterFormula);
            if (isDataFilterCompliant) {
                outputData.push(data);
            }
        }
        catch (err) {
            const formulaEvaluationError = {
                type: 'filterFormulaEvaluationError',
                formula: transformation.filterFormula,
                message: (0, common_1.getErrorMessage)(err),
                row: idx,
            };
            outputErrors.push(formulaEvaluationError);
            (0, common_1.debugLog)(formulaEvaluationError, err);
        }
    });
    return { errors: outputErrors, outputData };
};
const performTransform = async (params) => {
    try {
        const { dataTapeBuffers, transformation } = params;
        (0, common_1.finleyAssert)(dataTapeBuffers.length === 1, 'filter: Expected 1 data tape buffer');
        const srcDataTapeBuffer = dataTapeBuffers[0];
        const inputCsvArrayOutput = await common_1.csvConversion.finleyBufferToCsvArray({
            buffer: srcDataTapeBuffer,
            csvParser: params.csvParser,
        });
        const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, [transformation.filterFormula], undefined);
        const { errors, outputData } = await processFilter({
            transformation,
            inputCsvArray: inputCsvArrayOutput.csvArray,
            formulaEngine,
        });
        if (errors.length) {
            return {
                isValid: false,
                errors: consolidateToStructuredErrors(errors),
            };
        }
        const tapeDefinitionResult = validateTransform(transformation);
        (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, 'Invalid transformation');
        const outputDataTape = tapeDefinitionResult.data;
        (0, common_1.finleyAssert)(outputDataTape, 'Output data tape is not defined');
        const outputBuffer = (0, utils_1.rowDataToCsv)(outputData, outputDataTape);
        return { isValid: true, data: { outputBuffer }, supplementals: [] };
    }
    catch (error) {
        return (0, errorUtils_1.convertErrorToTapeFailureResult)(error, 'filter: ');
    }
};
exports.transformOperation = {
    operation: 'filter',
    validateTransform,
    performTransform,
};
//# sourceMappingURL=filter-transform.js.map